import { apiFetch } from "./api";

let cached: PushSubscription | null = null;
let inFlight: Promise<PushSubscription | null> | null = null;

// 10s upper bound on navigator.serviceWorker.ready: if SW registration failed
// at boot (network error fetching sw.js, etc.) the await would hang forever
// and every subsequent ensureSubscription call would also hang on the same
// stuck inFlight promise.
const SW_READY_TIMEOUT_MS = 10_000;

// Web Push VAPID keys are URL-safe base64 with no padding; PushManager.subscribe
// needs a raw Uint8Array. RFC 8292.
function urlBase64ToUint8Array(b64: string): Uint8Array {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

// Inverse of urlBase64ToUint8Array — encode the p256dh/auth ArrayBuffers from
// PushSubscription.getKey() for transmission to the backend.
function arrayBufferToUrlBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function ensureSubscription(): Promise<PushSubscription | null> {
  if (cached) return cached;
  if (inFlight) return inFlight;
  inFlight = doEnsureSubscription().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function doEnsureSubscription(): Promise<PushSubscription | null> {
  if (
    typeof window === "undefined" ||
    !("serviceWorker" in navigator) ||
    !("PushManager" in window) ||
    !("Notification" in window)
  ) {
    return null;
  }

  const rawKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
  if (!rawKey) return null;

  if (Notification.permission === "denied") return null;
  if (Notification.permission === "default") {
    const result = await Notification.requestPermission();
    if (result !== "granted") return null;
  }

  const reg = await withTimeout(
    navigator.serviceWorker.ready,
    SW_READY_TIMEOUT_MS,
    "serviceWorker.ready timed out",
  );
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(rawKey).buffer as ArrayBuffer,
    });
  }
  cached = sub;
  return sub;
}

function withTimeout<T>(p: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const id = setTimeout(() => reject(new Error(message)), ms);
    p.then(
      (v) => {
        clearTimeout(id);
        resolve(v);
      },
      (err: unknown) => {
        clearTimeout(id);
        reject(err);
      },
    );
  });
}

export async function subscribeStationNotifications(stationId: string): Promise<void> {
  const sub = await ensureSubscription();
  if (!sub) return;
  const p256dh = sub.getKey("p256dh");
  const auth = sub.getKey("auth");
  if (!p256dh || !auth) return;
  await apiFetch<void>("/notifications/subscribe", {
    method: "POST",
    auth: true,
    body: {
      transport: "web_push",
      endpoint: sub.endpoint,
      p256dh: arrayBufferToUrlBase64(p256dh),
      auth: arrayBufferToUrlBase64(auth),
      station_id: stationId,
    },
  });
}

export async function unsubscribeStationNotifications(stationId: string): Promise<void> {
  // DELETE is auth-scoped — always call it, even when in-memory state is empty
  // (e.g., after a page reload before any subscribe has happened).
  await apiFetch<void>(
    `/notifications/subscribe/${encodeURIComponent(stationId)}`,
    { method: "DELETE", auth: true },
  );
}

// Drop the cached PushSubscription AND tear down the browser-level subscription.
// Call this on sign-out: PushManager state persists across auth sessions, so
// without the unsubscribe() the next authed user would re-fetch the previous
// user's subscription and POST it under their own access token (403 against
// the token-owner guard, silently swallowed by the fire-and-forget caller).
export async function clearCachedSubscription(): Promise<void> {
  cached = null;
  inFlight = null;
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    await sub?.unsubscribe();
  } catch {
    // Best-effort: cache is already cleared, browser-level cleanup is bonus.
  }
}
